// Period Date: a per-metric date chosen ROW BY ROW from conditions on other columns, used
// to group the metric by its Year / Month (or any date part) and, optionally, to decide
// which rows fall inside the reporting period. See MetricPeriodDate in ../types.ts.
//
// This MIRRORS ReportSqlBuilder.PeriodDateExpr / PeriodDatePeriodClauses on the backend.
// The backend builds the SQL that runs; this builds the SQL shown in the preview tab - keep
// the two derivations identical (case order, COALESCE fallback, coarse pre-filter).
import type { ReportLogicContext } from "./context";
import type {
    DataColumn,
    DateGranularity,
    ExtraBreakdown,
    MetricPeriodDate,
    MetricSection,
    PeriodDateCase,
    ReportConfig,
} from "../types";
import {
    breakdownColumnForModel,
    columnMatchesKey,
    columnsMatch,
    dataColumnSelectionKey,
    findTimestampColumn,
    normalizeColumnIdentity,
    timestampColumnsForModel,
} from "./catalogResolution";
import { dateSqlForColumn } from "./sqlExpressions";
import { periodClauseFor } from "./periodBounds";
import { dateGranularityExpr, granularityLabel } from "./reportingPeriods";
import { collectRuleNodeIssues, metricFilterScope, ruleNodesExpression, ruleNodesOutline } from "./ruleInterpretation";
import type { RuleNodeIssue } from "./ruleInterpretation";

const PERIOD_DATE_LABEL = "Period Date";
const PERIOD_DATE_ALIAS = "PeriodDate";

function isPeriodDateExtra(extra?: ExtraBreakdown | null) {
    return extra?.source === "periodDate";
}

function hasPeriodDate(section: MetricSection) {
    return !!section.periodDate;
}

/** The dates a Period Date may pick from: every timestamp column on the metric's model. */
function periodDateColumns(context: ReportLogicContext, section: MetricSection): DataColumn[] {
    return timestampColumnsForModel(context, section.model);
}

function periodDateColumn(context: ReportLogicContext, section: MetricSection, key: string | undefined): DataColumn | null {
    if (!key) return null;
    return periodDateColumns(context, section).find((column) => columnMatchesKey(column, key)) || null;
}

/** Defaults: no rules yet, "otherwise" = the report's anchor date for this model, both toggles on. */
function makePeriodDate(context: ReportLogicContext, report: ReportConfig, section: MetricSection): MetricPeriodDate {
    const anchor = section.model ? findTimestampColumn(context.dataModels[section.model], report.modelBasedOn[section.model]) : null;
    return {
        cases: [],
        elseDateColumn: dataColumnSelectionKey(anchor),
        fallbackWhenNull: true,
        applyToReportingPeriod: true,
    };
}

function makePeriodDateCase(id: string): PeriodDateCase {
    return { id, conditionLogic: "and", conditions: [], dateColumn: "" };
}

function periodDateCaseCondition(context: ReportLogicContext, report: ReportConfig, section: MetricSection, item: PeriodDateCase, useIds = false) {
    return ruleNodesExpression(context, metricFilterScope(report, section), item.conditions, item.conditionLogic === "or" ? "or" : "and", useIds);
}

type ResolvedCase = { item: PeriodDateCase; column: DataColumn; condition: string };

// Cases that produce SQL. An incomplete case is skipped here (and reported by
// periodDateIssues, which also keeps the metric from being "ready") - the backend refuses
// to run it instead, since silently dropping a case re-dates rows.
function resolvedCases(context: ReportLogicContext, report: ReportConfig, section: MetricSection, useIds = false): ResolvedCase[] {
    return (section.periodDate?.cases || [])
        .map((item) => {
            const column = periodDateColumn(context, section, item.dateColumn);
            const condition = periodDateCaseCondition(context, report, section, item, useIds);
            return column && condition ? { item, column, condition } : null;
        })
        .filter(Boolean) as ResolvedCase[];
}

/**
 * The derived date as one SQL expression, or null when the Period Date is not usable
 * (no model, no "otherwise" date). With no usable case it is just the "otherwise" date.
 *
 *   CASE
 *     WHEN <case 1 conditions> THEN COALESCE(<case 1 date>, <otherwise date>)
 *     ...
 *     ELSE <otherwise date>
 *   END
 */
function periodDateExpr(context: ReportLogicContext, report: ReportConfig, section: MetricSection, useIds = false): string | null {
    const periodDate = section.periodDate;
    if (!periodDate || !section.model) return null;
    const elseColumn = periodDateColumn(context, section, periodDate.elseDateColumn);
    if (!elseColumn) return null;
    const elseSql = dateSqlForColumn(elseColumn);
    const branches = resolvedCases(context, report, section, useIds).map(({ column, condition }) => {
        const dateSql = dateSqlForColumn(column);
        const value = periodDate.fallbackWhenNull && !columnsMatch(column, elseColumn) ? `COALESCE(${dateSql}, ${elseSql})` : dateSql;
        return `  WHEN ${condition} THEN ${value}`;
    });
    if (!branches.length) return elseSql;
    return ["CASE", ...branches, `  ELSE ${elseSql}`, "END"].join("\n");
}

/** Distinct date columns the derived date can come from, in case order, "otherwise" last. */
function periodDateSourceColumns(context: ReportLogicContext, report: ReportConfig, section: MetricSection): DataColumn[] {
    const elseColumn = periodDateColumn(context, section, section.periodDate?.elseDateColumn);
    const columns = resolvedCases(context, report, section).map((item) => item.column);
    if (elseColumn) columns.push(elseColumn);
    return columns.filter((column, index) => columns.findIndex((other) => columnsMatch(other, column)) === index);
}

const wrapMultiline = (sql: string) => (sql.includes("\n") ? `(${sql})` : sql);

/**
 * The reporting-period WHERE clauses for a metric whose Period Date also drives the period,
 * or null to keep the report's anchor-date clause.
 *
 * A CASE in WHERE is not sargable, so when the date can come from more than one column a
 * coarse "any of the candidate dates is in the period" test comes first. It is implied by
 * the exact test (the derived date is always one of those columns), so it never changes
 * the result - it only lets SQL Server use indexes on the date columns.
 */
function periodDatePeriodClauses(context: ReportLogicContext, report: ReportConfig, section: MetricSection, useIds = false): string[] | null {
    if (!section.periodDate?.applyToReportingPeriod) return null;
    const expr = periodDateExpr(context, report, section, useIds);
    if (!expr) return null;
    const exact = periodClauseFor(context, report, section.model, wrapMultiline(expr));
    const columns = periodDateSourceColumns(context, report, section);
    if (columns.length <= 1) return [`${exact} -- Period Date decides the reporting period for this metric`];
    const coarse = columns.map((column) => periodClauseFor(context, report, section.model, dateSqlForColumn(column)));
    return [
        `(${coarse.join("\n  OR ")}) -- Period Date: coarse pre-filter so indexes on the date columns stay usable`,
        `${exact} -- Period Date decides the reporting period for this metric`,
    ];
}

/** SELECT/GROUP BY expression for one source === "periodDate" extra breakdown. */
function periodDateDimension(periodDateSql: string | null, extra: ExtraBreakdown) {
    if (!periodDateSql || !extra.granularity) return null;
    const expr = dateGranularityExpr(periodDateSql, extra.granularity);
    return { select: expr.select, groupBy: expr.groupBy, alias: `${PERIOD_DATE_ALIAS}_${granularityLabel(extra.granularity) || extra.granularity}` };
}

function periodDateGranularities(section: MetricSection): DateGranularity[] {
    return section.extraBreakdowns.filter(isPeriodDateExtra).map((extra) => extra.granularity).filter(Boolean) as DateGranularity[];
}

function setPeriodDateGranularity(section: MetricSection, granularity: DateGranularity, enabled: boolean, newId: string): MetricSection {
    const present = section.extraBreakdowns.some((extra) => isPeriodDateExtra(extra) && extra.granularity === granularity);
    if (enabled && !present) {
        return { ...section, extraBreakdowns: [...section.extraBreakdowns, { id: newId, column: "", groups: [], granularity, source: "periodDate" }] };
    }
    if (!enabled && present) {
        return { ...section, extraBreakdowns: section.extraBreakdowns.filter((extra) => !(isPeriodDateExtra(extra) && extra.granularity === granularity)) };
    }
    return section;
}

/** Turns the Period Date on with Year + Month grouping, or off (dropping its breakdowns). */
function enablePeriodDate(context: ReportLogicContext, report: ReportConfig, section: MetricSection, yearId: string, monthId: string): MetricSection {
    if (section.periodDate) return section;
    const withDate = { ...section, periodDate: makePeriodDate(context, report, section) };
    if (section.displayAs === "detail") return withDate;
    return setPeriodDateGranularity(setPeriodDateGranularity(withDate, "year", true, yearId), "month", true, monthId);
}

function removePeriodDate(section: MetricSection): MetricSection {
    const { periodDate, ...rest } = section;
    return { ...rest, extraBreakdowns: section.extraBreakdowns.filter((extra) => !isPeriodDateExtra(extra)) };
}

/** Header/label for any extra breakdown, Period Date ones included. */
function extraBreakdownLabel(context: ReportLogicContext, section: MetricSection, extra: ExtraBreakdown) {
    const granularityText = extra.granularity ? granularityLabel(extra.granularity) || "" : "";
    if (isPeriodDateExtra(extra)) return granularityText ? `${PERIOD_DATE_LABEL} (${granularityText})` : PERIOD_DATE_LABEL;
    const column = breakdownColumnForModel(context, section.model, extra.column);
    const columnLabel = column?.label || extra.column;
    const showGranularity = granularityText && normalizeColumnIdentity(columnLabel) !== normalizeColumnIdentity(granularityText);
    return `${columnLabel}${showGranularity ? ` (${granularityText})` : ""}`;
}

export interface PeriodDateIssue {
    nodeId: string;
    message: string;
}

function periodDateIssues(context: ReportLogicContext, report: ReportConfig, section: MetricSection): PeriodDateIssue[] {
    const periodDate = section.periodDate;
    if (!periodDate) return [];
    const issues: PeriodDateIssue[] = [];
    const scope = metricFilterScope(report, section);

    periodDate.cases.forEach((item, index) => {
        const label = `Date rule ${index + 1}`;
        if (!item.dateColumn) {
            issues.push({ nodeId: item.id, message: `${label}: choose which date to use when it matches.` });
        } else if (!periodDateColumn(context, section, item.dateColumn)) {
            issues.push({ nodeId: item.id, message: `${label}: its date column is no longer available on this data model.` });
        }
        const nodeIssues: RuleNodeIssue[] = [];
        collectRuleNodeIssues(context, scope, item.conditions, 0, [], nodeIssues);
        nodeIssues.forEach((issue) => issues.push({ nodeId: issue.nodeId, message: `${label}: ${issue.message}` }));
        if (!periodDateCaseCondition(context, report, section, item)) {
            issues.push({ nodeId: `${item.id}-conditions`, message: `${label} has no usable condition yet. Add one, or remove the rule - an incomplete rule would re-date rows.` });
        }
    });

    if (!periodDate.elseDateColumn) {
        issues.push({ nodeId: `${section.id}-period-else`, message: "Choose the date to use when no date rule matches." });
    } else if (!periodDateColumn(context, section, periodDate.elseDateColumn)) {
        issues.push({ nodeId: `${section.id}-period-else`, message: "The \"Otherwise\" date column is no longer available on this data model." });
    }

    const granularities = periodDateGranularities(section);
    if (section.displayAs !== "detail" && !granularities.length && !periodDate.applyToReportingPeriod) {
        issues.push({ nodeId: `${section.id}-period-parts`, message: "Tick at least one date part to group by, or use this date for the reporting period - otherwise the Period Date does nothing." });
    }
    const duplicate = granularities.find((granularity, index) => granularities.indexOf(granularity) !== index);
    if (duplicate) {
        issues.push({ nodeId: `${section.id}-period-parts`, message: `${granularityLabel(duplicate)} of the Period Date is added more than once.` });
    }
    return issues;
}

/** Period Date breakdowns left behind without a Period Date (e.g. applied from a template). */
function orphanPeriodDateExtras(section: MetricSection) {
    return section.periodDate ? [] : section.extraBreakdowns.filter(isPeriodDateExtra);
}

function isPeriodDateReady(context: ReportLogicContext, report: ReportConfig, section: MetricSection) {
    return !periodDateIssues(context, report, section).length && !orphanPeriodDateExtras(section).length;
}

/** Plain-English read-back, e.g. "by cancelled date when Trip Status Is Any Of (Cancelled); otherwise by trip date". */
function periodDateNarrative(context: ReportLogicContext, report: ReportConfig, section: MetricSection): string | null {
    const periodDate = section.periodDate;
    if (!periodDate) return null;
    const elseColumn = periodDateColumn(context, section, periodDate.elseDateColumn);
    const scope = metricFilterScope(report, section);
    const cases = periodDate.cases
        .map((item) => {
            const column = periodDateColumn(context, section, item.dateColumn);
            const outline = ruleNodesOutline(context, scope, item.conditions, item.conditionLogic === "or" ? "or" : "and");
            return column && outline ? `by ${column.label.toLowerCase()} when ${outline}` : "";
        })
        .filter(Boolean);
    const otherwise = elseColumn ? `by ${elseColumn.label.toLowerCase()}` : "by a date that has not been chosen yet";
    const dating = cases.length ? `${cases.join("; ")}; otherwise ${otherwise}` : otherwise;
    const fallback = cases.length && periodDate.fallbackWhenNull ? ` (falling back to ${elseColumn?.label.toLowerCase() || "the otherwise date"} when the chosen date is empty)` : "";
    return `${dating}${fallback}`;
}

export {
    PERIOD_DATE_LABEL,
    PERIOD_DATE_ALIAS,
    isPeriodDateExtra,
    hasPeriodDate,
    periodDateColumns,
    periodDateColumn,
    makePeriodDate,
    makePeriodDateCase,
    periodDateCaseCondition,
    periodDateExpr,
    periodDateSourceColumns,
    periodDatePeriodClauses,
    periodDateDimension,
    periodDateGranularities,
    setPeriodDateGranularity,
    enablePeriodDate,
    removePeriodDate,
    extraBreakdownLabel,
    periodDateIssues,
    orphanPeriodDateExtras,
    isPeriodDateReady,
    periodDateNarrative,
};
